import { CommonModule } from '@angular/common';
import { Component, OnInit, } from '@angular/core';
import { NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { LanguageService } from './core/modules/translate/services/language.service';
import { AuthService } from './core/services/auth.service';
import { UserRole } from './features/chat/enums/userRole.enum';
import { ChatHubService } from './features/chat/services/chat-hub.service';
import { NotificationBadgeComponent } from './features/shared/components/notification-badge/notification-badge.component';
import { NotificationService } from './features/shared/services/notification.service';
import { UserModel } from './features/users/models/users.model';
import { SharedModule } from './features/shared/shared.module';
import { Languages } from './core/enums/languages';
import { ProjectsService } from './features/projects/services/projects.service';
import { ProjectModel } from './features/projects/models/project.model';
import { CustomToastrService } from './features/shared/services/custom-toastr.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterModule, CommonModule, TranslateModule, NotificationBadgeComponent, SharedModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  private destroy$ = new Subject<void>();
  hideLayout: boolean;
  currentUser: UserModel;
  isOnChatPage: boolean;
  groupId: string;
  rejoinChat: boolean;
  currentLang: string;
  isRtl : boolean;

  constructor(
    private route: Router,
    private languageService: LanguageService,
    private chatHubService: ChatHubService,
    private toster : CustomToastrService,
    private translate : TranslateService,
    protected notificationService: NotificationService,
    private projectService : ProjectsService,
    private authService: AuthService) {
  }

  async ngOnInit(): Promise<void> {
    this.languageService.initAppLanguage();
    this.languageService.currentLanguage.subscribe(c => this.currentLang = c);

    if (this.authService.isAuthenticated()) {
      await this.authService.fetchCurrentUser();
      this.authService.loadPermissions();


      this.initializeChatSocket();
    }

    this.logout();

    this.route.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.hideLayout = event.url.includes('login');
        this.isOnChatPage = event.urlAfterRedirects.includes('chat');
        this.notificationService.setOnChatPage(this.isOnChatPage);
      }
    });

  }

  logout() {
    if (!this.authService.currentUser) {
      setTimeout(() => {
        if (!this.route.url.includes('login')) {
          this.route.navigate(['/login']);
        }
      }, 500);
    }
  }

  async initializeChatSocket(): Promise<void> {
    this.currentUser = this.authService.currentUser;

    await this.chatHubService.startConnection(this.currentUser.id.toString());

    this.chatHubService.chatAssigned$
      .pipe(takeUntil(this.destroy$))
      .subscribe(status => this.handleChatAssignment(status));

    this.chatHubService.groupId$.pipe(takeUntil(this.destroy$))
      .subscribe(groupId => this.groupId = groupId);

    this.chatHubService.rejoinedChat$.pipe(takeUntil(this.destroy$))
      .subscribe(status => this.rejoinChat = status);

    this.chatHubService.chatProjectId$.pipe(takeUntil(this.destroy$))
      .subscribe(projectId => this.getproject(projectId));

      this.chatHubService.chatClosed$.pipe(takeUntil(this.destroy$))
      .subscribe(status => {
        if (status && !this.isOnChatPage) {
          this.isRtl = this.currentLang === Languages.AR
          this.toster.showToast('info', '',this.translate.instant('chat.chatClosed'));
          this.notificationService.resetUnreadMessageCount();
          this.chatHubService.resetChatState();
        }
      });
      }

  async handleChatAssignment(status: boolean): Promise<void> {

    var isAgent = await this.authService.hasPermission(UserRole[UserRole.Agent]);

    if (status && isAgent && !this.rejoinChat && this.groupId) {
      this.chatHubService.sendWelcomeMessage(this.groupId, this.currentUser!.id);
      this.notificationService.notifyNewChatAssigned();
      setTimeout(() => this.notificationService.clearNewChatNotification(), 10000);
    }
  }

  changeLanguage(lang: string): void {
    this.currentLang = lang;
    this.languageService.changeLanguage(lang);
  }

  goToChat() {
    this.route.navigate(['/chat']);
    this.notificationService.resetUnreadMessageCount();
  }

  getproject(projectId: number) {
      if (!projectId) return;
      this.projectService.get<ProjectModel>("details/" + projectId).subscribe((res: ProjectModel) => {
        this.chatHubService.setProjectName( this.isRtl ? res.nameAr : res.name);
      })
    }
}
